// The DHTN logo for the web (founder, 10 Oct 2026). The mark: a 2x2 grid of squares lettered D H / T N,
// all four Iznik Blue with white DHTN Sans Black letters on every screen, only the four outer corners
// rounded (radius 28), a 6-unit gap (11 Oct 2026; squares of 103 in a 212 mark). `variant="lockup"` adds a thin rule and two rows of capitals,
// DHTN (Black) over ACADEMY (SemiBold), each centred on its row of squares; the words are font outlines
// (logoWords.ts, scripts/brand-vectorize/lockup_words.py), so the logo never waits for the web font.
// `variant="symbol"` is the mark alone. The mark is written by
// scripts/brand-vectorize/grid_mark.py (letters 44 on a 100 square, grown with the square). The rule and the words follow the screen: ink on light,
// white on dark.
//
// The lockup's box ends at ACADEMY's baseline (the mark's bottom 20 units hang below it, overflow
// visible), so in a row aligned on baselines the header links stand on the same line as ACADEMY.
// Its height is therefore 192.3/212 of the mark: a 36px mark is a 33px lockup.

import { useId } from 'react'
import { BOTTOM_BASELINE, LOCKUP_WIDTH, RULE_X, WORD_ACADEMY, WORD_DHTN } from './logoWords'
import './DhtnLogo.css'

interface DhtnLogoProps {
    className?: string
    /** `auto` follows the site theme. `dark`/`light` name the surface the logo sits on. */
    surface?: 'auto' | 'dark' | 'light'
    /** `lockup` = mark + DHTN / ACADEMY (web headers). `symbol` = the mark alone. */
    variant?: 'lockup' | 'symbol'
    /** The mark's motion (founder, 10 Oct 2026, "Shine": quiet and premium). A soft light sweeps across the
        four squares. `rest`: on arrival, then every 10 seconds (headers). `loop`: without rest (loading).
        Anyone who asks for less motion sees the still mark. */
    shine?: 'rest' | 'loop'
}

const BLUE = '#0B5CFF'
export const LETTERS_DN = 'M36.23 74.16Q34.51 74.16 33.28 72.9Q32.05 71.64 32.05 69.89V33.11Q32.05 31.36 33.28 30.1Q34.51 28.84 36.23 28.84H48.73Q58.69 28.84 64.82 35.05Q70.95 41.25 70.95 51.36V51.61Q70.95 61.81 64.82 67.98Q58.69 74.16 48.73 74.16ZM44.65 62.7H47.78Q52.51 62.79 55.34 59.95Q58.17 57.11 58.17 51.91V51.3Q58.17 46.17 55.34 43.47Q52.51 40.76 47.78 40.76H44.65ZM145.42 183.13Q143.94 183.13 142.89 182.03Q141.83 180.93 141.83 179.36V142.54Q141.83 140.58 143.14 139.21Q144.45 137.84 146.32 137.84H151.17Q152.38 137.84 153.45 138.5Q154.52 139.17 155.21 140.31L167.29 160.47H167.54L167.33 153.66V141.52Q167.33 140.04 168.36 138.97Q169.4 137.9 170.88 137.9H175.61Q177.06 137.9 178.11 138.97Q179.17 140.04 179.17 141.52V178.91Q179.17 180.69 178.01 181.92Q176.85 183.16 175.16 183.16H170.82Q169.71 183.16 168.79 182.56Q167.87 181.95 167.36 181.17L153.74 159.78H153.46L153.71 166.56V179.36Q153.71 180.93 152.65 182.03Q151.6 183.13 150.15 183.13Z'
export const LETTERS_HT = 'M170.98 74.16Q169.44 74.16 168.34 73.03Q167.23 71.9 167.23 70.33V32.67Q167.23 31.1 168.34 29.97Q169.44 28.84 170.98 28.84H175.93Q177.47 28.84 178.56 29.97Q179.64 31.1 179.64 32.67V70.33Q179.64 71.9 178.56 73.03Q177.47 74.16 175.93 74.16ZM145.07 74.16Q143.56 74.16 142.46 73.03Q141.36 71.9 141.36 70.33V32.67Q141.36 31.1 142.46 29.97Q143.56 28.84 145.07 28.84H150.02Q151.56 28.84 152.65 29.97Q153.74 31.1 153.74 32.67V70.33Q153.74 71.9 152.65 73.03Q151.56 74.16 150.02 74.16ZM148.54 57.07V45.23H172.49V57.07ZM49.03 183.16Q47.48 183.16 46.38 182.02Q45.29 180.88 45.29 179.29V144.33H57.77V179.29Q57.77 180.88 56.66 182.02Q55.55 183.16 54.03 183.16ZM37.12 149.75Q35.69 149.75 34.66 148.68Q33.62 147.62 33.62 146.19V141.4Q33.62 139.94 34.66 138.89Q35.69 137.84 37.12 137.84H65.88Q67.31 137.84 68.34 138.89Q69.38 139.94 69.38 141.4V146.19Q69.38 147.62 68.34 148.68Q67.31 149.75 65.88 149.75Z'

// Outer corners rounded (radius 28), inner corners square: D top-left, H top-right, T bottom-left,
// N bottom-right.
export const SQUARE_D = 'M28 0H103V103H0V28A28 28 0 0 1 28 0Z'
export const SQUARE_H = 'M109 0H184A28 28 0 0 1 212 28V103H109Z'
export const SQUARE_T = 'M0 109H103V212H28A28 28 0 0 1 0 184Z'
export const SQUARE_N = 'M109 109H212V184A28 28 0 0 1 184 212H109Z'

// The words' and the rule's colour (via currentColor): ink on light screens, white on dark ones.
const TONE = {
    auto: 'text-[#0E1014] dark:text-white',
    light: 'text-[#0E1014]',
    dark: 'text-white',
}

const DhtnLogo = ({ className = 'h-7', surface = 'auto', variant = 'symbol', shine }: DhtnLogoProps) => {
    const lockup = variant === 'lockup'
    const id = useId().replace(/[^a-zA-Z0-9_-]/g, '')
    return (
        <svg
            viewBox={lockup ? `0 0 ${LOCKUP_WIDTH} ${BOTTOM_BASELINE}` : '0 0 212 212'}
            aria-hidden="true"
            focusable="false"
            className={`block w-auto shrink-0 select-none overflow-visible ${TONE[surface]} ${className}`}
        >
            <path fill={BLUE} d={`${SQUARE_D}${SQUARE_H}${SQUARE_T}${SQUARE_N}`} />
            <path fill="#FFFFFF" d={`${LETTERS_DN}${LETTERS_HT}`} />
            {shine && <>
                <defs>
                    <clipPath id={`${id}c`}><path d={`${SQUARE_D}${SQUARE_H}${SQUARE_T}${SQUARE_N}`} /></clipPath>
                    <linearGradient id={`${id}g`} x1="0" x2="1">
                        <stop offset="0" stopColor="#fff" stopOpacity="0" />
                        <stop offset=".5" stopColor="#fff" stopOpacity=".55" />
                        <stop offset="1" stopColor="#fff" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <g clipPath={`url(#${id}c)`}>
                    <g transform="skewX(-18)">
                        <rect className={`dhtn-shine dhtn-shine--${shine}`} x={-200} y={-40} width={90} height={300} fill={`url(#${id}g)`} />
                    </g>
                </g>
            </>}
            {lockup && <>
                {/* A hairline at any size: one screen pixel, never scaled. */}
                <line x1={RULE_X} y1={17} x2={RULE_X} y2={195} stroke="currentColor" strokeOpacity={0.3} strokeWidth={1} vectorEffect="non-scaling-stroke" />
                <path fill="currentColor" d={`${WORD_DHTN}${WORD_ACADEMY}`} />
            </>}
        </svg>
    )
}

export default DhtnLogo
